import { NgClass } from '@angular/common';
import { Component, computed, inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputErrorComponent } from '@common/components/input/input-error/input-error.component';
import { InputLabelComponent } from '@common/components/input/input-label/input-label.component';
import { TranslatePipe } from '@ngx-translate/core';
import { NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { formatNumber, NumberFormatOptions } from '@progress/kendo-angular-intl';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BaseInputComponent } from '../base-input/base-input.component';
import { NumberInputConfig } from '../input.type';

/**
 * Number input component.
 * @param min - The minimum number.
 * @param max - The maximum number.
 * @param format - The format of the number.
 * @param decimals - The number of decimals.
 * @param maxLength - The maximum length of the number.
 * @param showSpinners - Whether to show spinners(Up and Down arrows) in edit mode.
 */
@Component({
    selector: 'app-number-input',
    templateUrl: './number-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: NumberInputComponent }],
    imports: [
        InputLabelComponent,
        NumericTextBoxComponent,
        TranslatePipe,
        NgClass,
        InputErrorComponent,
        FormsModule,
        TooltipDirective
    ]
})
export class NumberInputComponent extends BaseInputComponent<number> implements OnInit {
    @Input() min: NumberInputConfig['min'] = 0;
    @Input() max: NumberInputConfig['max'];
    @Input() format: NumberInputConfig['format'] = '#';
    @Input() decimals: NumberInputConfig['decimals'];
    @Input() maxLength: NumberInputConfig['maxLength'];
    @Input() showSpinners: NumberInputConfig['showSpinners'] = false;
    @Input() percentage: NumberInputConfig['percentage'] = false;

    private locale = inject(LOCALE_ID);
    private hasExplicitFormat = false;
    private viewFormat: string | NumberFormatOptions;

    displayValue = computed(() => {
        const value = this.value();
        if (value == null) return '';

        if (this.hasExplicitFormat) {
            try {
                return formatNumber(value, this.viewFormat, this.locale);
            } catch {
                return value.toString();
            }
        }

        return value.toString();
    });

    ngOnInit(): void {
        if (typeof this.format !== 'string') {
            this.hasExplicitFormat = true;
            this.viewFormat = this.format;
            return;
        }

        if (this.percentage) {
            this.hasExplicitFormat = true;
            // Custom percentage format where the number is not scaled (5 becomes 5%, not 0.05 → 5%).
            // Reference: https://www.telerik.com/kendo-angular-ui/components/knowledge-base/numerictextbox-percentage
            this.format = `#.## \\%`;
            this.viewFormat = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
            return;
        }

        if (this.decimals != null) {
            this.hasExplicitFormat = true;
            this.format = `n${this.decimals}`;
            this.viewFormat = { minimumFractionDigits: this.decimals, maximumFractionDigits: this.decimals };
        }
    }

    onInput(event: any): void {
        if (this.maxLength) {
            const value = event.target.value;
            const [intPart, decPart] = value.split(',');
            if (intPart.length > this.maxLength) {
                const newValue = `${intPart.slice(0, this.maxLength)}${value.includes(',') ? `,${decPart}` : ''}`;
                event.target.value = newValue;
            }
        }
    }
}
